import { Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react'
import { FC } from 'react'
import { MdSavings } from 'react-icons/md'
import { Button } from '@/components/ui/button'
import { currencyFormat } from '@/utils/currencyFormat'
import { badgeStyle, REUSE_COLORS, withAlpha } from '@/utils/reuseBrand'
import { StatusColor } from '@/utils/StatusColor.utility'
import { EXCLUDED_FROM_RETOMAS_STATUS_IDS, NEGATIVE_FINAL_STATUS_IDS, UNCLASSIFIED_STATUS_IDS } from '@/utils/tradeinStatusGroups'
import { IStatusIndicador, KPI_ACCENTS } from './ListCard'
import StatusCard from './StatusCard'

interface RetomasBreakdownModalProps {
  isOpen: boolean
  onClose: () => void
  listIndicadores: IStatusIndicador[]
  loading: boolean
  montoRetomas: number
  trendPct: number | null
  sparkline: number[]
}

const RetomasBreakdownModal: FC<RetomasBreakdownModalProps> = ({ isOpen, onClose, listIndicadores, loading, montoRetomas, trendPct, sparkline }) => {
  const rows = listIndicadores
    .map((row) => ({ ...row, cantidad: Number(row.cantidad ?? 0), monto: Number(row.monto ?? 0) }))
    .filter((row) => row.cantidad > 0)

  const included = rows.filter((row) => !EXCLUDED_FROM_RETOMAS_STATUS_IDS.includes(row.id_status)).sort((a, b) => b.monto - a.monto)
  /* rechazos: cuentan para el mensaje de "no incluye X rechazados" */
  const negative = rows.filter((row) => NEGATIVE_FINAL_STATUS_IDS.includes(row.id_status))
  /* estados aún no clasificados (ej. Caducado): solo informativo, no se suman a nada */
  const unclassified = rows.filter((row) => UNCLASSIFIED_STATUS_IDS.includes(row.id_status))

  const negativeMonto = negative.reduce((acc, row) => acc + row.monto, 0)
  const negativeCantidad = negative.reduce((acc, row) => acc + row.cantidad, 0)
  const unclassifiedCantidad = unclassified.reduce((acc, row) => acc + row.cantidad, 0)

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => { if (!open) onClose() }} size='xl' placement='center' backdrop='blur' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <span className='flex h-9 w-9 items-center justify-center rounded-lg' style={{ backgroundColor: withAlpha(REUSE_COLORS.reBlue, 0.12), color: REUSE_COLORS.reBlue }}>
              <MdSavings className='text-xl' />
            </span>
            <div className='flex flex-col'>
              <span className='text-base font-semibold'>Total en retomas</span>
              <span className='text-tiny font-normal text-slate-400'>Monto vigente, sin contar rechazos</span>
            </div>
          </div>
        </ModalHeader>
        <ModalBody className='pb-6'>
          <StatusCard
            loading={loading}
            indicador={montoRetomas}
            formatValue={(value) => currencyFormat(value, import.meta.env.VITE_COUNTRY)}
            estado='Total en retomas'
            Icon={<MdSavings size={40} />}
            accentColor={KPI_ACCENTS.retomas}
            trendPct={trendPct}
            sparkline={sparkline}
          />

          <div className='mt-4 flex flex-col gap-1'>
            <p className='text-tiny font-semibold uppercase tracking-wide text-slate-400 mb-1'>Desglose por estado</p>
            {included.length === 0
              ? <p className='text-sm text-slate-400 py-2'>Sin datos para mostrar.</p>
              : included.map((row) => (
                <div key={row.id_status} className='flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2.5'>
                  <div className='flex items-center gap-2'>
                    <span className='h-2.5 w-2.5 rounded-full shrink-0' style={{ backgroundColor: StatusColor(row.status_name, true) }} />
                    <span className='text-sm font-medium text-slate-700'>{row.status_name}</span>
                    <span className='text-tiny rounded-md px-1.5 py-0.5' style={badgeStyle(StatusColor(row.status_name, true), 0.14)}>{row.cantidad}</span>
                  </div>
                  <span className='text-sm font-semibold text-slate-700'>{currencyFormat(row.monto, import.meta.env.VITE_COUNTRY)}</span>
                </div>
                ))}
          </div>

          {negativeCantidad > 0 && (
            <p className='mt-4 text-tiny text-slate-400'>
              No incluye {negativeCantidad} trade-in{negativeCantidad === 1 ? '' : 's'} rechazado{negativeCantidad === 1 ? '' : 's'} / no aprobado{negativeCantidad === 1 ? '' : 's'}, equivalentes a {currencyFormat(negativeMonto, import.meta.env.VITE_COUNTRY)}.
            </p>
          )}

          {unclassifiedCantidad > 0 && (
            <p className='mt-1 text-tiny text-slate-300'>
              Además hay {unclassifiedCantidad} cotización{unclassifiedCantidad === 1 ? '' : 'es'} caducada{unclassifiedCantidad === 1 ? '' : 's'} (solo informativo, no se contabiliza).
            </p>
          )}

          <div className='mt-5 flex justify-end'>
            <Button size='sm' onClick={onClose}>Cerrar</Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default RetomasBreakdownModal
